import React from 'react';
import I18n from 'i18n-js';
import { core_apimessages_Money as Money } from '@reverbdotcom/commons/src/gql/graphql';
import { RCTooltip } from '@reverbdotcom/cadence/components';
import ProductFinishSVG from '../../images/icons/product-finish.svg';
import SmallPriceGuideGraphIcon from '../../images/price-guide-tool/small-white-price-guide-graph.svg';

interface ICardNudgeProps {
  imgSrc?: string;
  imgAlt?: string;
  textContent: string | React.ReactNode;
}

interface IProductFinishesNudgeProps {
  finishes: string[];
  textContent?: string | React.ReactNode;
}
interface IPriceGuideRecommendationNudgeProps {
  lowPriceRecommendation: Money;
  highPriceRecommendation: Money;
  noLabel?: boolean;
}

function CardNudge({ imgSrc, imgAlt = '', textContent }: ICardNudgeProps) {
  return (
    <div className="card-nudge">
      {imgSrc && <img src={imgSrc} alt={imgAlt} className="card-nudge__icon" />}
      <div className="card-nudge__text">
        {textContent}
      </div>
    </div>
  );
}

function finishCount(finishes: string[]) {
  return finishes.length || 0;
}

function getFinishes(finishes: string[]) {
  return finishes.reduce((list, finish) => {
    return `${list},\n${finish}`;
  });
}

export function getFinishesTooltipText(finishes: string[]) {
  const count = finishCount(finishes);

  if (count < 7) return getFinishes(finishes);

  const sample = finishes.slice(0, 5);
  const truncatedFinishes = getFinishes(sample);

  return `${truncatedFinishes},\n${I18n.t('discovery.cardNudge.more', { amount: `${count - sample.length}` })}`;
}

export function ProductFinishesNudge({ finishes, textContent = undefined }: IProductFinishesNudgeProps) {
  if (finishCount(finishes) < 2) return null;

  return (
    <CardNudge
      imgSrc={ProductFinishSVG}
      imgAlt={I18n.t('discovery.cardNudge.multipleFinishes')}
      textContent={
        <RCTooltip text={getFinishesTooltipText(finishes)} type="underline" stopPropagation>
          <span className="card-nudge__text--underline">
            {textContent || I18n.t('discovery.cardNudge.multipleFinishes')}
          </span>
        </RCTooltip>
      }
    />
  );
}

export function PriceGuideRecommendationNudge({
  lowPriceRecommendation,
  highPriceRecommendation,
  noLabel = false,
}: IPriceGuideRecommendationNudgeProps) {
  if (!lowPriceRecommendation || !highPriceRecommendation) return null;

  return (
    <CardNudge
      imgSrc={SmallPriceGuideGraphIcon}
      imgAlt={I18n.t('discovery.cardNudge.priceRecommendation.graphIconAlt')}
      textContent={
        <RCTooltip text={I18n.t('discovery.cardNudge.priceRecommendation.tooltipText')} type="underline" stopPropagation>
          <span className="card-nudge__text--underline">
            {noLabel ?
              `${lowPriceRecommendation.display}-${highPriceRecommendation.display}` :
              `${I18n.t('discovery.cardNudge.priceRecommendation.tooltipLabel')} ${lowPriceRecommendation.display}-${highPriceRecommendation.display}`
            }
          </span>
        </RCTooltip>
      }
    />
  );
}

export function TradeInNudge({ isTradeInEligible = false }: { isTradeInEligible: boolean; }) {
  if (!isTradeInEligible) return null;

  return (
    <div
      className="ptb-1 plr-2 bd-radius-md"
      style={{
        backgroundColor: 'var(--rc-color-background-alert-info)',
        width: 'fit-content',
      }}
    >
      <CardNudge
        textContent={
          <div
            className="weight-bold"
            style={{ color: 'var(--rc-color-text-info)' }}
          >
            {I18n.t('discovery.cardNudge.tradeIn.nudge')}
          </div>
        }
      />
    </div>
  );
}
